import React from 'react';
import {Form, Col, Row} from 'react-bootstrap';
import ChartCommentRichTextEditor from '@/views/storyboard/form_fields/ChartCommentRichTextEditor';
import {
    chartHeights,
    chartWidths,
} from '../utils/chartConstants';
import {ChartQuery, ChartSettings} from 'entities/chart';
import {FormikT} from 'utils/types';
import {ChartSettingsTitleEditor} from '@/views/storyboard/chart_settings/ChartSettingsTitleEditor';
import {ChartSettingsSegmentedControl} from '@/views/storyboard/chart_settings/ChartSettingsSizeControls';
import * as yup from 'yup';
import i18n from 'i18n';

interface Props {
    chartQueryFormik: FormikT<ChartQuery>;
    chartSettingsFormik: FormikT<ChartSettings>;
    chartId?: number;
    showTitleRequiredError?: boolean;
}

export const ChartCommentSettingsSchema = yup.object({
    title: yup
        .string()
        .required(i18n.t('chart_comment_settings_form_required_field.text')),
    description: yup
        .string()
        .max(512, i18n.t('chart_description_cannot_be_longer.text'))
        .default(''),
});

const ChartCommentSettingsForm = React.memo<Props>((
    {
        chartId,
        chartQueryFormik,
        chartSettingsFormik,
        showTitleRequiredError = false,
    },
) => {
    const widthOptions = chartWidths.map(([value, label]) => ({value, label}));
    const heightOptions = chartHeights.map(([value, label]) => ({value, label}));

    return (
        <Form>
            <Row>
                <Col xs={12}>
                    <div className="chart-settings-comment-title">
                        <ChartSettingsTitleEditor
                            chartSettingsFormik={chartSettingsFormik}
                            boxedDisplay
                            showTitleRequiredError={showTitleRequiredError}
                        />
                    </div>

                    <Form.Group className="mb-3" controlId="additionalData.comment" >
                        <ChartCommentRichTextEditor
                            chartId={chartId ?? null}
                            HTMLValue={chartQueryFormik.values.additionalData.comment || ''}
                            onChange={(HTMLDump) => chartQueryFormik.setFieldValue('additionalData.comment', HTMLDump)}
                        />
                    </Form.Group>

                    <Row>
                        <Col xs={6}>
                            <Form.Group
                                controlId="chartWidth"
                                className="inline-chart-width-radio-group m-r-10 mb-3"
                                data-cy="comment-settings-form-comment-width"
                            >
                                <Form.Label className="d-block">{i18n.t('chart_comment_width.text')}</Form.Label>
                                <ChartSettingsSegmentedControl
                                    value={chartSettingsFormik.values.width}
                                    options={widthOptions}
                                    onChange={(value) => chartSettingsFormik.setFieldValue('width', value)}
                                    ariaLabel={i18n.t('chart_comment_width.text')}
                                />
                                {chartSettingsFormik.touched.width && chartSettingsFormik.errors.width ? (
                                    <div className="invalid-feedback d-block mt-1">
                                        {chartSettingsFormik.errors.width}
                                    </div>
                                ) : null}
                            </Form.Group>
                        </Col>

                        <Col xs={6}>
                            <Form.Group
                                controlId="chartHeight"
                                className="inline-chart-height-radio-group m-l-10 mb-3"
                                data-cy="comment-settings-form-comment-height"
                            >
                                <Form.Label className="d-block">{i18n.t('chart_comment_height.text')}</Form.Label>
                                <ChartSettingsSegmentedControl
                                    value={chartSettingsFormik.values.height}
                                    options={heightOptions}
                                    onChange={(value) => chartSettingsFormik.setFieldValue('height', value)}
                                    ariaLabel={i18n.t('chart_comment_height.text')}
                                />
                                {chartSettingsFormik.touched.height && chartSettingsFormik.errors.height ? (
                                    <div className="invalid-feedback d-block mt-1">
                                        {chartSettingsFormik.errors.height}
                                    </div>
                                ) : null}
                            </Form.Group>
                        </Col>
                    </Row>
                </Col>
            </Row>
        </Form >
    );
});

export default ChartCommentSettingsForm;
